import * as React from 'react';
import { Button } from '@vechaiui/react';
import { Form } from 'formik';
import { Link } from 'react-router-dom';
import { useLocalization } from '@fluent/react';
import { useMutation } from '@apollo/client';
import { useToggle } from '@react-hookz/web';

import Dialog from '#/components/controls/Dialog';
import Formik from '#/components/forms/Formik';
import LoginLayout from '#/layouts/LoginLayout';
import RequestPasswordResetForm from '#/components/forms/RequestPasswordResetForm';
import { ErrorBlockFormik } from '#/components/forms/ErrorBlock';
import { FORGOT_PASSWORD_INITIAL_VALUES } from '#/constants/form-initial-values';
import { PublicRoute } from '#/constants/page-route';
import { RequestPasswordResetDocument } from '#/__generated__/graphql/graphql';
import { forgotPasswordSchema } from '#/constants/validation-schema';

const RequestPasswordResetPage = () => {
  const [requestResetMutation] = useMutation(RequestPasswordResetDocument);
  const { l10n } = useLocalization();

  const [isDialogOpen, toggleDialog] = useToggle(false);

  const [apiErrors, setApiErrors] = React.useState<{ message: string }[]>();

  return (
    <LoginLayout>
      <div>
        <Formik
          initialValues={FORGOT_PASSWORD_INITIAL_VALUES}
          onSubmit={async (formData, { resetForm }) => {
            try {
              await requestResetMutation({ variables: { input: formData } });
              resetForm();
              toggleDialog(true);
            } catch (err: any) {
              if (err.errors) {
                setApiErrors(err.errors);
              } else if (err.networkError?.result.errors) {
                setApiErrors(err.networkError.result.errors);
              } else {
                setApiErrors([err]);
              }
              alert(!!apiErrors && apiErrors[0].message.replace('ApolloError:', ''));
            }
          }}
          validationSchema={forgotPasswordSchema}
        >
          {(formikProps) => (
            <Form>
              <header className="mb-8">
                <h1 className="text-3xl font-bold">{l10n.getString('request-password-reset')}</h1>
              </header>

              <RequestPasswordResetForm />

              <footer className="mt-8">
                <div className="space-y-6">
                  {!formikProps.isValid && formikProps.submitCount > 0 && <ErrorBlockFormik />}

                  <div className="flex flex-col gap-y-5">
                    <Button
                      className="w-full"
                      color="primary"
                      size="xl"
                      type="submit"
                      variant="solid"
                    >
                      {l10n.getString('confirm')}
                    </Button>
                    <Link to={PublicRoute.Login}>
                      <Button className="w-full" size="xl" type="submit" variant="solid">
                        {l10n.getString('back')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </footer>
            </Form>
          )}
        </Formik>
      </div>
      <Dialog
        isOpen={isDialogOpen}
        closeButton={l10n.getString('close')}
        onClose={() => toggleDialog(false)}
        title={`${l10n.getString('request-password-reset')}`}
      >
        <div className="py-3 border-t border-black">
          <p className="pb-5">{l10n.getString('request-password-reset-message')}</p>
        </div>
      </Dialog>
    </LoginLayout>
  );
};

export default RequestPasswordResetPage;
